import { ChangeDetectorRef, OnDestroy, Pipe, PipeTransform } from '@angular/core';
import { Subscription } from 'rxjs';
import { DigitGrouping, DigitGroupingKeys, GeneralService, NumberFormats } from '@gs-2.0/core';

@Pipe({
  name: 'gsNumberFormat',
  standalone: true,
  pure: false,
})
export class NumberFormatPipe implements PipeTransform, OnDestroy {
  private subscription: Subscription;

  constructor(
    private generalService: GeneralService,
    private cdr: ChangeDetectorRef,
  ) {
    this.subscription = this.generalService.settings$.subscribe(() => {
      this.cdr.markForCheck();
    });
  }

  transform(value: number | null | undefined, decimals = 2): string {
    if (value === null || value === undefined) return '0';

    const { decimalChar, groupingSeparator, groupingKey } = this.getFormatConfig();

    const fixed = value.toFixed(decimals);
    const [intPart, decPart] = fixed.split('.');

    const grouped = this.applyGrouping(intPart, groupingKey, groupingSeparator);

    return decimals > 0 ? `${grouped}${decimalChar}${decPart}` : grouped;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private getFormatConfig(): { decimalChar: string; groupingSeparator: string; groupingKey: string } {
    const config = this.generalService.settings?.response.globalConfig ?? [];

    const decimalKey   = this.parseKey(config.find(c => c.key === 'decimalSymbol')?.value);
    const groupSymKey  = this.parseKey(config.find(c => c.key === 'digitGroupingSymbol')?.value);
    const groupingKey  = this.parseKey(config.find(c => c.key === 'digitGrouping')?.value);

    return {
      decimalChar:       NumberFormats.find(f => f.key === decimalKey)?.value  ?? '.',
      groupingSeparator: NumberFormats.find(f => f.key === groupSymKey)?.value ?? ',',
      groupingKey:       groupingKey || DigitGroupingKeys.DIGIT_GROUPING_1,
    };
  }

  /** Stored value is JSON `{"key":"DOT","value":"."}` — extract the key. */
  private parseKey(raw: string | undefined): string {
    if (!raw) return '';
    try {
      const parsed = JSON.parse(raw);
      return parsed?.key ?? raw;
    } catch {
      return raw;
    }
  }

  private applyGrouping(intStr: string, groupingKey: string, sep: string): string {
    switch (groupingKey) {
      case DigitGroupingKeys.DIGIT_GROUPING_0:
        // No grouping: 123456789
        return intStr;

      case DigitGroupingKeys.DIGIT_GROUPING_1: {
        // Groups of 3 from right: 1,234,567,890
        const chunks: string[] = [];
        let i = intStr.length;
        while (i > 0) {
          chunks.unshift(intStr.slice(Math.max(0, i - 3), i));
          i -= 3;
        }
        return chunks.join(sep);
      }

      case DigitGroupingKeys.DIGIT_GROUPING_2:
        // Last 3 only: 1234567,890
        return intStr.length > 3
          ? intStr.slice(0, -3) + sep + intStr.slice(-3)
          : intStr;

      case DigitGroupingKeys.DIGIT_GROUPING_3: {
        // Indian: last 3, then groups of 2: 12,34,56,789
        if (intStr.length <= 3) return intStr;
        const parts: string[] = [intStr.slice(-3)];
        let rest = intStr.slice(0, -3);
        while (rest.length > 0) {
          parts.unshift(rest.slice(-2));
          rest = rest.slice(0, -2);
        }
        return parts.join(sep);
      }

      default:
        return intStr;
    }
  }
}
